import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Landmark, Copy, ArrowRight, Banknote } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RelmInvoice() {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [amount, setAmount] = useState('');
  const [email, setEmail] = useState('');
  const [paying, setPaying] = useState(false);
  const [wire, setWire] = useState(null);

  useEffect(() => {
    axios.get(`${API}/relm/invoice/${invoiceId}`)
      .then((r) => {
        setInv(r.data);
        if (!r.data.open_amount) {
          setAmount(String(r.data.amount));
          axios.post(`${API}/relm/invoice/${invoiceId}/create-order`, { amount: r.data.amount, method: 'wire' })
            .then((res) => { if (res.data.mode === 'wire') setWire(res.data); })
            .catch(() => {});
        }
      })
      .catch(() => setNotFound(true));
  }, [invoiceId, searchParams]);

  const pay = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setPaying(true);
    try {
      const r = await axios.post(`${API}/relm/invoice/${invoiceId}/create-order`, { amount: amt, payer_email: email, method: 'wire' });
      if (r.data.mode === 'wire') setWire(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load payment instructions');
    } finally {
      setPaying(false);
    }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success('Copied'); };

  if (notFound) return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="relm-invoice-notfound">Invoice not found.</div>;
  if (!inv) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>;

  const ben = inv.beneficiary || {};

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="relm-invoice-page">
      <div className="max-w-xl mx-auto space-y-4">
        <div className="flex items-center gap-2 justify-center mb-2">
          <Landmark className="w-6 h-6 text-cyan-600" />
          <span className="text-xl font-bold tracking-tight">EzyTransfer</span>
        </div>
        <Card className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide">Payment request</p>
              <h1 className="text-2xl font-bold" data-testid="relm-invoice-no">{inv.invoice_no}</h1>
            </div>
            <Badge variant="outline" className="uppercase" data-testid="relm-invoice-status">{inv.status}</Badge>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Billed to</span><span className="font-medium text-right" data-testid="relm-invoice-payer">{inv.payer_name}</span></div>
            {inv.payer_address && <div className="flex justify-between gap-6"><span className="text-slate-500">Address</span><span className="text-right text-xs max-w-[320px]" data-testid="relm-invoice-address">{inv.payer_address}</span></div>}
            <div className="flex justify-between"><span className="text-slate-500">Beneficiary</span><span className="font-medium" data-testid="relm-invoice-beneficiary">{ben.account_name} (USD)</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Description</span><span className="text-right">{inv.description}</span></div>
            <div className="flex justify-between border-t pt-2 mt-2">
              <span className="text-slate-500">Amount</span>
              <span className="font-bold" data-testid="relm-invoice-amount">{inv.open_amount ? 'Open — enter below' : `$ ${Number(inv.amount).toFixed(2)} USD`}</span>
            </div>
          </div>
        </Card>

        {!wire ? (
          <Card className="p-6 space-y-4">
            {inv.open_amount && (
              <div>
                <Label>Amount to pay (USD)</Label>
                <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 500.00" data-testid="relm-pay-amount-input" />
              </div>
            )}
            <div>
              <Label>Your email (for the receipt)</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="relm-pay-email-input" />
            </div>
            <Button className="w-full bg-cyan-600 hover:bg-cyan-700" onClick={pay} disabled={paying} data-testid="relm-pay-wire-btn">
              {paying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Banknote className="w-4 h-4 mr-2" />}
              Show USD account payment instructions
            </Button>
            <a href={`${API}/relm/invoice/${invoiceId}/pdf`} className="block text-center text-xs text-cyan-700 hover:underline" data-testid="relm-invoice-pdf-link">
              Download invoice (PDF)
            </a>
            <p className="text-[11px] text-slate-400 text-center">Secured by RELM (FINTRAC-regulated MSB). Pay by USD bank transfer directly into the beneficiary's USD account.</p>
          </Card>
        ) : (
          <Card className="p-6 space-y-3" data-testid="relm-wire-card">
            <h2 className="font-semibold">Pay direct to USD account</h2>
            <p className="text-xs text-cyan-800 bg-cyan-50 border border-cyan-200 rounded p-2">{wire.note}</p>
            {[
              ['Amount', `$ ${Number(wire.amount).toFixed(2)} USD`],
              ['Beneficiary', wire.beneficiary.account_name],
              ['Account number', wire.beneficiary.account_number],
              ['Wire routing (ABA)', wire.beneficiary.routing_number],
              ['Bank', wire.beneficiary.bank],
              ['Payment reference', wire.reference],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5">
                <span className="text-slate-500">{k}</span>
                <span className="font-mono flex items-center gap-2">{v}
                  <button onClick={() => copy(String(v))} className="text-slate-400 hover:text-slate-700" data-testid={`relm-wire-copy-${k.toLowerCase().replace(/[^a-z]+/g, '-')}`}><Copy className="w-3 h-3" /></button>
                </span>
              </div>
            ))}
            <p className="text-[11px] text-slate-400">Quote the payment reference so your payment is matched automatically.</p>
          </Card>
        )}
      </div>
    </div>
  );
}
